import { Box, useTheme } from "@mui/material";
import { DotLegalAlert } from "@dotlegal/dotlegal-ui-components";
import { useTranslation } from "@localization/useTranslation";
import { RiskExposureMode } from "./RiskExposure.types";
import { formatExposureValue } from "./formatExposureValue";
import { RiskExposureSummary } from "../riskOperationalOverview/RiskOperationalOverview.types";

export interface IRiskExposureBanner {
    exposure: RiskExposureSummary | null;
    exposureMode: RiskExposureMode;
    exposureCurrencyCode: string | null;
    exposureScaleUpperBound: number | null;
}

// The exposure headline over a risk table: gross → after-mitigation totals in Financial mode, averages in Scale
// mode. Renders nothing when exposure is not configured or nothing in the table carries a value yet, so a caller
// can place it unconditionally. Shared so every table that aggregates exposure states it the same way.
function RiskExposureBanner({ exposure, exposureMode, exposureCurrencyCode, exposureScaleUpperBound }: IRiskExposureBanner) {
    const theme = useTheme();
    const { translateString, translateNumber } = useTranslation();

    if (exposureMode === RiskExposureMode.None || exposure === null || exposure.withExposureCount === 0) return null;

    const financial = exposureMode === RiskExposureMode.Financial;
    // Never null here: the mode is not None and a missing total reads as zero rather than as "nothing captured".
    const format = (value: number | null) => formatExposureValue(value ?? 0, exposureMode, exposureCurrencyCode, exposureScaleUpperBound, translateNumber)!;

    const gross = format(financial ? exposure.grossSum : exposure.grossAverage);
    // With no active mitigation plans in view the after-mitigation figure just repeats gross, so the
    // "→ after mitigation" segment is dropped rather than shown as an identical number.
    const afterMitigation = exposure.afterMitigationPlanCount > 0 ? format(financial ? exposure.afterMitigationSum : exposure.afterMitigationAverage) : null;

    const numberSx = { fontWeight: theme.typography.fontWeightBold };

    return (
        <DotLegalAlert
            severity="info"
            marginBottom={2}
            warningText={
                <>
                    {translateString(financial ? "financialExposureBanner" : "averageExposure")}:{" "}
                    <Box component="span" sx={numberSx}>
                        {gross}
                    </Box>
                    {afterMitigation !== null && (
                        <>
                            {" → "}
                            {translateString("afterMitigationLower")}:{" "}
                            <Box component="span" sx={numberSx}>
                                {afterMitigation}
                            </Box>
                        </>
                    )}
                </>
            }
        />
    );
}

export default RiskExposureBanner;
